// ./src/components/generic/SectionTabs/sectionNav.ts
//
// The rules behind the phone section strip, as pure functions over a nav tree.
//
// ⚠⚠ THEY LIVE HERE, NOT INSIDE THE COMPONENT, SO A CHECK CAN RUN THEM. `npm run check-nav` feeds
// these the REAL sitemap and asserts which section each of a list of live paths resolves to — which
// it could only otherwise do by re-implementing the matching, and a guard that re-implements the
// rule passes happily while the component drops it. One implementation, exercised by both.
//
// No React, no services, no i18n — just the tree and a pathname.
// ⚠ TYPE-ONLY, so this module has NO runtime import at all and `check-nav` can load it in a
// plain tsx process without dragging the app's config (and its env reads) in behind it.
import type { SiteMapItem } from "@/config";

/** Below this the section is one page and a strip of one tab says nothing. */
export const MIN_TABS = 2;

/**
 * Above this a strip stops showing the section at a glance, which is its only advantage over the
 * "More" list — there, at least, everything is on one screen.
 *
 * ⚠ NINE, not eight, since Artists moved into Program. A nine-pill strip is still scannable
 * because the lit one centres itself; the account Library, at fourteen, is the case this genuinely
 * stands down for. If a section ever needs more than this, that is a sign the SECTION is wrong,
 * not that the number is.
 */
export const MAX_TABS = 9;

/**
 * Does `path` sit inside `base` — the same page, or one of its drawer stages?
 *
 * ⚠ SEGMENT-BOUNDARY, NOT `startsWith`. `/events/1/guests` is a plain string prefix of
 * `/events/1/guest-groups`, so a naive prefix test lights the wrong tab; and `/events/1` is a prefix
 * of every single page in the event workspace.
 */
export const isUnder = (path: string, base: string): boolean =>
  path === base || path.startsWith(base.endsWith("/") ? base : `${base}/`);

/** A child that is an actual destination — visible, and with a url of its own. */
export const isDestination = (item: SiteMapItem): boolean => item.visible !== false && !!item.url;

/** The visible, routable children of a group — what a strip would offer. */
export const sectionChildren = (group: SiteMapItem): SiteMapItem[] => (group.items ?? []).filter(isDestination);

/** Every destination in the nav — top-level leaves included — paired with the top-level item it sits under. */
const everyDestination = (navigationItems: SiteMapItem[]): { url: string; top: SiteMapItem }[] => {
  const out: { url: string; top: SiteMapItem }[] = [];

  for (const top of navigationItems) {
    if (top.url) {
      out.push({ url: top.url, top });
    }

    for (const child of sectionChildren(top)) {
      out.push({ url: child.url!, top });
    }
  }

  return out;
};

/**
 * May `candidate` claim paths BELOW it, or only its own?
 *
 * ⚠⚠ THIS IS THE RULE THAT STOPS THE DASHBOARD OWNING THE WHOLE WORKSPACE. The Overview group's
 * Dashboard leaf is `/events/:eventId`, which is a prefix of EVERY page of that event — so plain
 * prefix matching handed Artists, Website, Theme, Compliance, Marketing Channels and fourteen more
 * an "Overview" strip offering to navigate away to Details and Schedule. Nineteen real routes, and
 * every one of them looked deliberate.
 *
 * A destination that is an ancestor of some OTHER section's destination is not a list with drawer
 * stages under it — it is a root that happens to sit high in the path. Those match exactly and
 * nothing else. A destination whose descendants all belong to its OWN section (Guest List, which
 * sits above Guest Groups, Prize Draws and every `…/guests/:id/edit` stage) keeps claiming them,
 * which is what makes a drawer keep its list's tab lit.
 */
const ownsDescendants = (candidate: string, top: SiteMapItem, all: { url: string; top: SiteMapItem }[]): boolean =>
  !all.some((d) => d.top !== top && d.url !== candidate && isUnder(d.url, candidate));

/**
 * The tabs to show for `currentPath`, or [] for none.
 *
 * ⚠ LONGEST MATCH WINS. Several children can contain the path — `/events/1/guests/groups` sits
 * under both Guest Groups and Guest List — and taking the longest url picks the page the host is
 * actually on.
 */
export const sectionTabsFor = (navigationItems: SiteMapItem[], currentPath: string): SiteMapItem[] => {
  const all = everyDestination(navigationItems);
  let best: { group: SiteMapItem; length: number } | null = null;

  for (const group of navigationItems) {
    const children = sectionChildren(group);

    if (children.length < MIN_TABS || children.length > MAX_TABS) {
      continue;
    }

    for (const child of children) {
      const hit =
        currentPath === child.url ||
        (isUnder(currentPath, child.url!) && ownsDescendants(child.url!, group, all));

      if (hit && (!best || child.url!.length > best.length)) {
        best = { group, length: child.url!.length };
      }
    }
  }

  if (!best) {
    return [];
  }

  // ⚠ ROADMAP A2b #63 — a page the event does not use (`navMore`, set by `markEventFeatureNav`) is not offered as a
  // tab: the strip shows what the menu shows. The one the host is ON stays, or the strip would light nothing.
  // ⚠ The section is still CHOSEN over all its pages (above), so arriving on a tucked-away page keeps its strip.
  const siblings = sectionChildren(best.group);
  const lit = activeTabUrl(siblings, currentPath);

  return siblings.filter((child) => !child.navMore || child.url === lit);
};

/**
 * Which tab is lit. Same longest-match rule, over the same list, so a drawer stage
 * (`…/guests/:id/edit`) keeps its list's tab lit instead of lighting nothing.
 */
export const activeTabUrl = (tabs: SiteMapItem[], currentPath: string): string | null => {
  // ⚠ No ancestor rule here, deliberately. Within ONE section every tab is a sibling of the others,
  // and a drawer stage under Guest List must light Guest List — longest match alone is right.
  let found: string | null = null;

  for (const tab of tabs) {
    if (isUnder(currentPath, tab.url!) && (!found || tab.url!.length > found.length)) {
      found = tab.url!;
    }
  }

  return found;
};
